class ListQuantityHandler
{
    constructor()
    {
        this.cartItemIds = new Map();

        this.init();
    }

    init()
    {
        this.hydrateCartItemIds();
        this.bindEvents();
        this.bindAddedToCartEvent();
    }

    hydrateCartItemIds()
    {
        const containers = document.querySelectorAll('.product-cart-actions[data-product-id]');

        for (const container of containers)
        {
            const controls = container.querySelector('.product-quantity-controls[data-direct-mode="true"]');

            if (!controls)
                continue;

            const productId = this.toInteger(container.dataset.productId);
            const updateForm = container.querySelector('.quantity-update-form');

            if (productId <= 0 || !updateForm)
                continue;

            const cartItemId = this.toInteger(updateForm.dataset.itemId);

            if (cartItemId > 0)
                this.cartItemIds.set(productId, cartItemId);
        }
    }

    bindAddedToCartEvent()
    {
        window.eventBus.subscribe('AddedToCart', (execution, data) =>
        {
            if (execution !== 'OnTrigger' || !data)
                return;

            const directMode = data.directmode === true || data.directmode === 'true';

            if (!directMode)
                return;

            const productId = this.toInteger(data.productid ?? data.productId);
            const cartItemId = this.toInteger(data.cartitemid ?? data.cartItemId ?? data.id);

            if (productId <= 0 || cartItemId <= 0)
                return;

            this.cartItemIds.set(productId, cartItemId);

            this.setCartItemId(productId, cartItemId);
        });
    }

    bindEvents()
    {
        document.addEventListener('click', (e) =>
        {
            const increaseButton = e.target.closest('.quantity-increase');

            if (increaseButton)
            {
                const controls = increaseButton.closest('.product-quantity-controls[data-direct-mode="true"]');

                if (!controls)
                    return;

                this.handleStepChange(controls, 1);

                return;
            }

            const decreaseButton = e.target.closest('.quantity-decrease');

            if (decreaseButton)
            {
                const controls = decreaseButton.closest('.product-quantity-controls[data-direct-mode="true"]');

                if (!controls)
                    return;

                this.handleStepChange(controls, -1);
            }
        });

        document.addEventListener('focusin', (e) =>
        {
            const input = e.target.closest('.product-quantity-controls[data-direct-mode="true"] .quantity-input');

            if (!input)
                return;

            input.dataset.previousValue = input.value;
        });

        document.addEventListener('blur', (e) =>
        {
            const input = e.target.closest('.product-quantity-controls[data-direct-mode="true"] .quantity-input');

            if (!input)
                return;

            if (input.dataset.ignoreBlurSubmit === 'true')
            {
                input.dataset.ignoreBlurSubmit = 'false';

                return;
            }

            this.commitInput(input);
        }, true);

        document.addEventListener('keydown', (e) =>
        {
            if (e.key !== 'Enter')
                return;

            const input = e.target.closest('.product-quantity-controls[data-direct-mode="true"] .quantity-input');

            if (!input)
                return;

            e.preventDefault();

            input.dataset.ignoreBlurSubmit = 'true';

            this.commitInput(input);

            input.blur();
        });
    }

    handleStepChange(controls, direction)
    {
        const input = controls.querySelector('.quantity-input');

        if (!input)
            return;

        const currentQuantity = this.toNumber(input.value);
        const step = this.getStep(input);

        let newQuantity = currentQuantity + (direction * step);

        if (newQuantity < 0)
            newQuantity = 0;

        const normalizedQuantity = this.roundToPrecision(newQuantity, this.getDecimalPlaces(step));

        this.applyQuantityChange(input, normalizedQuantity, currentQuantity);
    }

    commitInput(input)
    {
        const currentQuantity = this.toNumber(input.dataset.previousValue ?? input.value);
        const step = this.getStep(input);
        const normalizedQuantity = this.normalizeInputQuantity(input.value, step);

        this.applyQuantityChange(input, normalizedQuantity, currentQuantity);
    }

    async applyQuantityChange(input, newQuantity, previousQuantity)
    {
        const controls = input.closest('.product-quantity-controls[data-direct-mode="true"]');

        if (!controls)
            return;

        const container = controls.closest('.product-cart-actions[data-product-id]');

        if (!container)
            return;

        const productId = this.toInteger(container.dataset.productId);

        if (productId <= 0)
            return;

        const quantityUnit = this.getQuantityUnit(container);
        const previousValue = this.toDisplayNumber(previousQuantity);

        input.value = this.toDisplayNumber(newQuantity);

        try
        {
            const cartItemId = this.getCartItemId(productId, container);

            if (newQuantity <= 0)
            {
                if (cartItemId > 0)
                    await this.removeFromCart(productId, cartItemId, container);

                input.value = '0';
                input.dataset.previousValue = '0';

                return;
            }

            if (cartItemId > 0)
                await this.updateCart(container, newQuantity, cartItemId);
            else
                await this.addToCart(container, newQuantity, quantityUnit, productId);

            input.dataset.previousValue = this.toDisplayNumber(newQuantity);
        }
        catch (error)
        {
            console.error('Failed to process list quantity change:', error);

            input.value = previousValue;
            input.dataset.previousValue = previousValue;
        }
    }

    async addToCart(container, quantity, quantityUnit, productId)
    {
        const addForm = container.querySelector('.quantity-add-form');

        if (!addForm)
            throw new Error('Add form was not found for direct quantity mode.');

        addForm.dataset.quantity = this.toDisplayNumber(quantity);

        if (quantityUnit)
            addForm.dataset.quantityUnit = quantityUnit;

        await window.submitApiForm(addForm);

        const cartItemId = this.getCartItemId(productId, container);

        if (cartItemId <= 0)
            throw new Error('Cart item ID was not returned after add to cart.');

        this.setCartItemId(productId, cartItemId);
    }

    async updateCart(container, quantity, cartItemId)
    {
        const updateForm = container.querySelector('.quantity-update-form');

        if (!updateForm)
            throw new Error('Update form was not found for direct quantity mode.');

        updateForm.dataset.quantity = this.toDisplayNumber(quantity);
        updateForm.dataset.itemId = String(cartItemId);

        await window.submitApiForm(updateForm);
    }

    async removeFromCart(productId, cartItemId, container)
    {
        const response = await fetch(`${document.baseURI}api/cart/remove`,
        {
            method: 'DELETE',
            headers:
            {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                itemId: cartItemId
            })
        });

        if (!response.ok)
            throw new Error('Failed to remove item from cart.');

        this.cartItemIds.delete(productId);
        this.setCartItemId(productId, 0);

        const updateForm = container.querySelector('.quantity-update-form');

        if (updateForm)
            updateForm.dataset.quantity = '0';

        window.eventBus.broadcast('RemovedFromCart', 'OnTrigger',
        {
            itemId: cartItemId,
            productId: productId,
            quantity: 0,
            directMode: true
        });

        window.eventBus.broadcast('CartQuantityChanged', 'OnAjaxComplete',
        {
            productId: productId,
            quantity: 0,
            directMode: true
        });
    }

    getCartItemId(productId, container)
    {
        const mappedCartItemId = this.toInteger(this.cartItemIds.get(productId));

        if (mappedCartItemId > 0)
            return mappedCartItemId;

        const updateForm = container.querySelector('.quantity-update-form');

        if (!updateForm)
            return 0;

        return this.toInteger(updateForm.dataset.itemId);
    }

    setCartItemId(productId, cartItemId)
    {
        const containers = document.querySelectorAll(`.product-cart-actions[data-product-id="${productId}"]`);

        if (cartItemId > 0)
            this.cartItemIds.set(productId, cartItemId);
        else
            this.cartItemIds.delete(productId);

        for (const container of containers)
        {
            const updateForm = container.querySelector('.quantity-update-form');

            if (updateForm)
                updateForm.dataset.itemId = String(cartItemId);
        }
    }

    getQuantityUnit(container)
    {
        const addForm = container.querySelector('.quantity-add-form');

        if (addForm && addForm.dataset.quantityUnit)
            return addForm.dataset.quantityUnit;

        const updateForm = container.querySelector('.quantity-update-form');

        if (updateForm && updateForm.dataset.quantityUnit)
            return updateForm.dataset.quantityUnit;

        return 'ks';
    }

    getStep(input)
    {
        const step = this.toNumber(input.step);

        if (step > 0)
            return step;

        return 1;
    }

    normalizeInputQuantity(rawValue, step)
    {
        const value = this.toNumber(rawValue);

        if (value <= 0)
            return 0;

        const normalizedStep = step > 0
            ? step
            : 1;
        const multiplier = Math.ceil(value / normalizedStep);
        const precision = this.getDecimalPlaces(normalizedStep);

        return this.roundToPrecision(multiplier * normalizedStep, precision);
    }

    getDecimalPlaces(value)
    {
        if (!Number.isFinite(value))
            return 0;

        const valueString = value.toString();
        const index = valueString.indexOf('.');

        if (index === -1)
            return 0;

        return valueString.length - index - 1;
    }

    roundToPrecision(value, precision)
    {
        return Number(value.toFixed(precision));
    }

    toNumber(value)
    {
        const normalizedValue = this.normalizeNumericString(value);
        const parsed = Number.parseFloat(normalizedValue);

        if (Number.isFinite(parsed))
            return parsed;

        return 0;
    }

    normalizeNumericString(value)
    {
        const source = String(value ?? '').trim().replace(/\s+/g, '');

        if (source === '')
            return '0';

        const hasComma = source.includes(',');
        const hasDot = source.includes('.');

        if (hasComma && hasDot)
        {
            const lastCommaIndex = source.lastIndexOf(',');
            const lastDotIndex = source.lastIndexOf('.');

            if (lastCommaIndex > lastDotIndex)
                return source.replace(/\./g, '').replace(',', '.');

            return source.replace(/,/g, '');
        }

        if (hasComma)
            return source.replace(',', '.');

        return source;
    }

    toInteger(value)
    {
        const parsed = Number.parseInt(String(value), 10);

        if (Number.isInteger(parsed))
            return parsed;

        return 0;
    }

    toDisplayNumber(value)
    {
        if (!Number.isFinite(value))
            return '0';

        return String(value);
    }
}

document.addEventListener('DOMContentLoaded', () =>
{
    window.listQuantityHandler = new ListQuantityHandler();
});

export default ListQuantityHandler;
